import React from 'react';
import { Shield, Check, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const WarrantyPage = () => {
  const warrantyFeatures = [
    'Yellowing or discoloration',
    'Cracking, peeling, or delamination',
    'Hazing or loss of clarity',
    'Adhesive failure',
    'Bubbling or blistering under normal conditions',
    'Self-healing failure within operating specifications'
  ];

  return (
    <div className="min-h-screen pt-24 bg-[#050505]">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-2">Peace of Mind</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8]">
            10 Year Warranty
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Card */}
        <div className="bg-[#111111] border border-[#222222] p-8 md:p-12 mb-12">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#00F0FF] flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#050505]" />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-[#F8F8F8]">Vetrox Film Warranty</h2>
              <p className="text-[#00F0FF] font-bold">10 Years Coverage</p>
            </div>
          </div>
          <p className="text-[#A0A0A0] leading-relaxed">
            All DIY PPF Kits feature premium Vetrox paint protection film, backed by our comprehensive 
            10-year manufacturer warranty. This warranty covers material defects and ensures your 
            investment is protected for years to come.
          </p>
        </div>

        {/* What's Covered */}
        <div className="mb-12">
          <h3 className="text-xl font-medium text-[#F8F8F8] mb-6">What's Covered</h3>
          <div className="bg-[#111111] border border-[#222222] p-6">
            <p className="text-[#A0A0A0] mb-6">
              Our warranty covers the following defects in materials under normal use conditions:
            </p>
            <ul className="space-y-4">
              {warrantyFeatures.map((feature, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-[#00F0FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#050505]" />
                  </div>
                  <span className="text-[#F8F8F8]">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Warranty Terms */}
        <div className="mb-12">
          <h3 className="text-xl font-medium text-[#F8F8F8] mb-6">Warranty Terms</h3>
          <div className="space-y-4">
            <div className="bg-[#111111] border border-[#222222] p-6">
              <h4 className="text-[#F8F8F8] font-medium mb-3">Coverage Period</h4>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                The warranty period begins from the date of purchase and extends for 10 years. 
                Proof of purchase is required for all warranty claims.
              </p>
            </div>

            <div className="bg-[#111111] border border-[#222222] p-6">
              <h4 className="text-[#F8F8F8] font-medium mb-3">Transferable Warranty</h4>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                The warranty is transferable to subsequent vehicle owners within the warranty period. 
                The new owner must provide proof of the original purchase and vehicle transfer documentation.
              </p>
            </div>

            <div className="bg-[#111111] border border-[#222222] p-6">
              <h4 className="text-[#F8F8F8] font-medium mb-3">Claim Process</h4>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                To initiate a warranty claim, contact us at Sales@DIYPPFKit.com.au with your order 
                number, photos of the defect, and a description of the issue. Our team will assess 
                the claim and provide instructions for the next steps.
              </p>
            </div>
          </div>
        </div>

        {/* Exclusions */}
        <div className="mb-12">
          <h3 className="text-xl font-medium text-[#F8F8F8] mb-6">Warranty Exclusions</h3>
          <div className="bg-[#111111] border border-[#222222] p-6">
            <p className="text-[#A0A0A0] text-sm leading-relaxed mb-4">
              The warranty does not cover:
            </p>
            <ul className="space-y-2 text-[#A0A0A0] text-sm">
              <li>• Damage from improper installation</li>
              <li>• Physical damage from accidents, collisions, or vandalism</li>
              <li>• Damage from chemicals, abrasives, or pressure washing</li>
              <li>• Normal wear and tear from stone chips and road debris</li>
              <li>• Damage from car washes with brushes</li>
              <li>• Damage from extreme environmental conditions outside normal use</li>
              <li>• Cosmetic damage that does not affect film performance</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#1A1A1A] border border-[#222222] p-8 text-center">
          <FileText className="w-10 h-10 text-[#00F0FF] mx-auto mb-4" />
          <h3 className="text-xl font-medium text-[#F8F8F8] mb-3">Ready to Protect Your Vehicle?</h3>
          <p className="text-[#A0A0A0] mb-6">
            All our kits come with the full 10-year Vetrox warranty included.
          </p>
          <Link to="/products">
            <Button 
              className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-8 py-4 rounded-none"
              data-testid="warranty-build-kit-btn"
            >
              Build Your Kit
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPage;
